import { useQuery } from '@tanstack/react-query'
import { flatten, uniqBy } from 'lodash'

import { ISO } from '@freighthub/freighthub-corelib'

import Client from '@/api/client'
import { getConfig } from '@/app/config'
import {
  BookingDTOTransportMode,
  BookingDTOTransportType,
  IncotermDTO,
  IncotermDTOCode,
  IncotermDTONamedPlaceOfDelivery,
} from '@/clients/booking'

export type IncotermUniqueKey = Exclude<ISO.IncotermCode, 'FCA' | 'DAT'> | 'FCA_T' | 'FCA_LW'

export const mapIncotermToUniqueKey = (incoterm: IncotermDTO): IncotermUniqueKey => {
  if (
    incoterm.code === IncotermDTOCode.Fca &&
    incoterm.namedPlaceOfDelivery === IncotermDTONamedPlaceOfDelivery.Terminal
  ) {
    return 'FCA_T'
  }
  if (
    incoterm.code === IncotermDTOCode.Fca &&
    incoterm.namedPlaceOfDelivery === IncotermDTONamedPlaceOfDelivery.LoadingWarehouse
  ) {
    return 'FCA_LW'
  }
  return incoterm.code as IncotermUniqueKey
}

export type Incoterm = {
  key: IncotermUniqueKey
  code: IncotermDTOCode
  description: string
  namedPlaceOfDelivery?: IncotermDTONamedPlaceOfDelivery
}

export enum BookingParty {
  Buyer = 'buyer',
  Seller = 'seller',
}

export const incotermCodeToNamedPlaceOfDelivery: Partial<Record<IncotermUniqueKey, IncotermDTONamedPlaceOfDelivery>> = {
  FCA_LW: IncotermDTONamedPlaceOfDelivery.LoadingWarehouse,
  FCA_T: IncotermDTONamedPlaceOfDelivery.Terminal,
}

export type QuoteIncoterm = {
  code: IncotermUniqueKey
  description: string
}

export type IncotermMetadata = {
  automatedQuoteIncoterms: QuoteIncoterm[]
  manualQuoteIncoterms: QuoteIncoterm[]
  bookingParty: BookingParty
  destination: ('door' | 'port')[]
  origin: ('door' | 'port')[]
  preferredIncoterm: IncotermDTOCode
  transportMode: BookingDTOTransportMode[]
  transportType: BookingDTOTransportType[]
}

function mapIncotermMetadataToIncotermsList(incotermsMetadata: IncotermMetadata[]): Incoterm[] {
  const manualIncoterms = flatten(incotermsMetadata.map((incoTerm) => incoTerm.manualQuoteIncoterms))
  const automaticIncoterms = flatten(incotermsMetadata.map((incoTerm) => incoTerm.automatedQuoteIncoterms))

  return uniqBy([...automaticIncoterms, ...manualIncoterms], 'code').map((incoterm) => {
    const namedPlaceOfDelivery = incotermCodeToNamedPlaceOfDelivery[incoterm.code]

    return {
      key: incoterm.code,
      ...incoterm,
      ...(namedPlaceOfDelivery && {
        code: IncotermDTOCode.Fca,
        namedPlaceOfDelivery,
      }),
    } as Incoterm
  })
}

const basePath = '/api/shipping'
const client = new Client(getConfig().API_ENDPOINT ?? '')

function getIncotermsMetadata() {
  return client.get<IncotermMetadata[]>(`${basePath}/api/v1/quotes/incoterms`)
}

export const useIncotermsQuery = () => {
  return useQuery({
    queryKey: ['incoterms'],
    queryFn: async () => {
      const incotermsMetadata = await getIncotermsMetadata()
      return mapIncotermMetadataToIncotermsList(incotermsMetadata)
    },
    refetchOnMount: false,
  })
}
